import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setEmail('');
      return;
    }
    if (!email.trim()) return;

    setFormStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('email', email);
      const resp = await fetch('https://readdy.ai/api/form/d8uj1mgu8fvptuidq04g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!resp.ok) throw new Error('Submission failed');
      setFormStatus('success');
      setEmail('');
    } catch {
      setFormStatus('error');
    }
    setTimeout(() => setFormStatus('idle'), 4000);
  };

  return (
    <footer className="bg-foreground-950 text-background-50 rounded-t-3xl mt-0">
      <div className="container-page py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-3">
              Stay Updated
            </h3>
            <p className="text-foreground-300 text-sm md:text-base leading-relaxed mb-6 max-w-sm">
              Get the latest AllChinaBuy Spreadsheet updates, new seller alerts, and shopping tips delivered to you.
            </p>
            <form
              data-readdy-form
              id="newsletter-form"
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-2 max-w-md"
            >
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 bg-foreground-800 border border-foreground-700 rounded-lg text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Subscribe'}
                <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="mt-2 text-xs text-primary-400">Thanks for subscribing! We will keep you updated.</p>
            )}
            {formStatus === 'error' && (
              <p className="mt-2 text-xs text-accent-400">Something went wrong. Please try again.</p>
            )}
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm font-semibold text-foreground-300 uppercase tracking-wider mb-4">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/articles/best-allchinabuy-spreadsheet-2026"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                >
                  Best Spreadsheet 2026
                </Link>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                >
                  AllChinaBuy Sheet
                </a>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4"
                >
                  Buy It Now
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm font-semibold text-foreground-300 uppercase tracking-wider mb-4">
              Contact & Info
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-foreground-400">
                <i className="ri-mail-line mt-0.5 text-foreground-500"></i>
                <a href="mailto:hello@allchinabuyspreadsheet.ink" className="hover:text-background-50 transition-colors underline decoration-foreground-700 underline-offset-4">
                  hello@allchinabuyspreadsheet.ink
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-400">
                <i className="ri-global-line mt-0.5 text-foreground-500"></i>
                <span>Updated Daily for 2026</span>
              </li>
              <li className="pt-2">
                <h4 className="text-sm font-semibold text-foreground-300 uppercase tracking-wider mb-3">
                  About
                </h4>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  The largest AllChinaBuy Spreadsheet directory online. Trusted by thousands of buyers worldwide for verified sellers, updated product links, and comprehensive shopping guides.
                </p>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-10 mt-10 border-t border-foreground-800">
          <div className="flex items-center gap-6">
            <span className="text-sm text-foreground-400 whitespace-nowrap">
              <i className="ri-twitter-x-line mr-1"></i>Twitter
            </span>
            <span className="text-sm text-foreground-400 whitespace-nowrap">
              <i className="ri-reddit-line mr-1"></i>Reddit
            </span>
            <span className="text-sm text-foreground-400 whitespace-nowrap">
              <i className="ri-discord-line mr-1"></i>Discord
            </span>
          </div>
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} AllChinaBuySpreadsheet.ink. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-foreground-400 whitespace-nowrap">
              Privacy Policy
            </span>
            <span className="text-xs text-foreground-400 whitespace-nowrap">
              Terms of Use
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}